import React, { useState, useEffect } from 'react';
import { Instagram } from 'lucide-react';

// Instagram post IDs from @thepbg
const POST_IDS = [
  'DRReKXnEfJd',
  'DNLHPVrxTPv',
  'DM-mtdnSwm6',
  'DMNrkOgRZex',
  'DR6fUPLkTvA',
  'DR2E1lHkRgj',
  'DRzwmXPkvWT',
  'DRxQpdWEuES',
  'DRrY4vwEZE6',
  'DRrUAY5kYND',
  'DRpRx7-EVZ9',
  'DRU3seEkjmR'  
];

const InstagramGallery = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInstagramPosts = async () => {
      if (POST_IDS.length === 0) {
        setLoading(false);
        return;
      }

      const createFallbackPosts = () => {
        return POST_IDS.map((postId, index) => ({
          id: postId,
          thumbnail: `/assets/images/gallery/gallery-${(index % 4) + 1}.jpg`,
          permalink: `https://www.instagram.com/p/${postId}/`,
          caption: '',
          author: '@thepbg'
        }));
      };

      try {
        // Use PHP endpoint to fetch Instagram data (bypasses CORS)
        const response = await fetch('/api/instagram.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ postIds: POST_IDS }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.posts && data.posts.length > 0) {
            // Filter out posts that only have placeholder images
            const validPosts = data.posts.filter(post => 
              post.thumbnail && 
              !post.thumbnail.includes('/assets/images/gallery/') &&
              post.thumbnail.startsWith('http')
            );
            
            if (validPosts.length > 0) {
              setPosts(validPosts);
            } else {
              // All posts have placeholder images - use fallback
              setPosts(createFallbackPosts());
            }
          } else {
            // Fallback to placeholder images
            setPosts(createFallbackPosts());
          }
        } else {
          // Fallback to placeholder images
          setPosts(createFallbackPosts());
        }
      } catch (error) {
        // Silently fail - use fallback
        setPosts(createFallbackPosts());
      } finally {
        setLoading(false);
      }
    };

    fetchInstagramPosts();
  }, []);

  return (
    <section id="gallery" className="py-24 bg-gradient-to-br from-[#1a1918] to-[#2d2826]">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <Instagram className="w-8 h-8 text-[#d4af37]" />
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              See Us In Action
            </h2>
          </div>
          <p className="text-lg text-[#bbb5ae] max-w-2xl mx-auto mb-6">
            Watch our premium photo booths creating memorable moments at events
          </p>
          <a
            href="https://www.instagram.com/thepbg/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 text-[#d4af37] hover:text-[#ffd700] transition-colors duration-300 font-semibold"
          >
            <span>Follow @thepbg</span>
            <Instagram className="w-5 h-5" />
          </a>
        </div>

        {/* Instagram Posts Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#d4af37]"></div>
            <p className="text-[#bbb5ae] mt-4">Loading Instagram feed...</p>
          </div>
        ) : posts.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {posts.map((post, index) => (
              <a
                key={post.id}
                href={post.permalink}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative overflow-hidden rounded-lg cursor-pointer hover-lift"
                style={{
                  aspectRatio: '1/1',
                  animationDelay: `${index * 0.1}s`,
                  opacity: 0,
                  animation: 'fadeInUp 0.8s ease-out forwards'
                }}
              >
                <img
                  src={post.thumbnail}
                  alt={post.caption || `Instagram post from ${post.author}`}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                  loading="lazy"
                  onError={(e) => {
                    // If image fails, try fallback placeholder
                    const fallbackIndex = POST_IDS.indexOf(post.id) % 4;
                    const fallbackImage = `/assets/images/gallery/gallery-${fallbackIndex + 1}.jpg`;
                    if (e.target.src !== fallbackImage) {
                      e.target.src = fallbackImage;
                    } else {
                      // If fallback also fails, show placeholder background
                      e.target.style.display = 'none';
                      e.target.parentElement.style.backgroundColor = '#1a1918';
                      e.target.parentElement.style.backgroundImage = 'linear-gradient(135deg, #1a1918 0%, #2d2826 100%)';
                    }
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a1918] via-[#1a1918]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="bg-[#d4af37] rounded-full p-4">
                    <Instagram className="w-8 h-8 text-[#1a1918] fill-current" />
                  </div>
                </div>
              </a>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white/5 rounded-lg border border-[#d4af37]/20 p-8 max-w-2xl mx-auto">
            <Instagram className="w-12 h-12 text-[#d4af37] mx-auto mb-4" />
            <p className="text-[#bbb5ae] mb-2 text-lg font-semibold">
              Instagram feed coming soon!
            </p>
            <p className="text-[#736c64] text-sm mb-4">
              To display posts, add Instagram post IDs to the component.
            </p>
            <p className="text-[#736c64] text-xs">
              Visit <a href="https://www.instagram.com/thepbg/" target="_blank" rel="noopener noreferrer" className="text-[#d4af37] hover:underline">@thepbg</a> on Instagram, 
              open a post, and copy the post ID from the URL (the part after /p/).
            </p>
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-12">
          <a
            href="https://www.instagram.com/thepbg/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-gradient-to-r from-[#d4af37] to-[#ffd700] text-[#1a1918] px-8 py-4 rounded-lg font-semibold hover:shadow-lg hover:shadow-[#d4af37]/50 transition-all duration-300 shine-effect"
          >
            View More on Instagram
          </a>
        </div>
      </div>
    </section>
  );
};

export default InstagramGallery;
